import React, { PropTypes } from "react";
import { FormattedHTMLMessage, FormattedMessage } from "react-intl";
import { Field } from "redux-form";
import { MessageBox } from "../../../../common";
import {
  RadioGroup,
  StyledCheckboxInput,
  SelectInput,
} from "../../../../common/form";
import { GM } from "../../enums/businessAccountTypes";
import { EN } from "../../../../../components/common/enums/languages";

import userHasAccess from "../../../../../utils/userHasAccess";
import { FINTECH, HIGH_MANAGEMENT_WITH_FINANCE_AND_MANAGER, EXTERNAL_ROLES, DIRECTOR, INTERNAL_ROLES } from "../../../../../utils/Roles";
import {
  AUTOMATIC,
  MANUAL,
} from "../../enums/automaticMemberIdentifierOptions";
import {
  ELIGIBILITY,
  TED_DESJARDINS
} from "../../enums/synchronizationOptions";
import { MEMBERS_VALIDATION_RULES } from "../../client/members/membersValidationRules";
import { IMPLEMENTATION, ACTIVE, TERMINATED } from "../../enums/clientStatusTypes";

const ClientAdditionalConfigurations = (props) => {
  const userLanguage = localStorage.getItem("languageCode") || EN.value;

  // Group field access control - only non-external roles can see the group field
  const canEditGroup = userHasAccess(DIRECTOR);
  let isGroupFieldDisabled =
      props.disabled ||
      !canEditGroup ||
      props.isArchived === true;

  if(userHasAccess(FINTECH)) {
    isGroupFieldDisabled = isGroupFieldDisabled ||  props.currentClientStatusType === TERMINATED.value
  }
  else {
    isGroupFieldDisabled = isGroupFieldDisabled ||
     !(props.currentClientStatusType === IMPLEMENTATION.value || props.currentClientStatusType === ACTIVE.value)
  }
  const resolvedGroupOptions = canEditGroup ? props.groupOptions : props.currentGroupOption;

  const getOptions = () => {
    let options = [];
    const max_length = MEMBERS_VALIDATION_RULES.memberIdentifier.maxLength.value;
    for (let i = 8, l = max_length; i <= l; i++) {
      let incr = ("0" + i.toString()).slice(-2);
      options.push({ value: i, text: incr });
    }
    return options;
  };

  const checkForSyncTypeChange = (value) => {
    if (value && typeof value == "object") {
      value = Object.values(value)
        .filter((v) => typeof v == "string")
        .join("");
    }
    if (value === ELIGIBILITY.value && !userHasAccess(FINTECH)) {
      props.changeFieldValue("client", "clientNumber", null);
      props.setClientNumberExistsState(false);
    }
  };

  const generateAutomaticIdentifierOptions = () => {
    const configOptions = props.create ? props.automaticIdentifierConfigurationTypes.map(type => ({
      value: type,
      key: `clients.form.additionalConfigurations.${type.toLowerCase()}Option`
    })) : [MANUAL, AUTOMATIC];
    return configOptions;
  };
  const generateOptions = () => {
    if (
      props.memberIdentifierType &&
      props.memberIdentifierType != undefined &&
      props.automaticMemberIdentifierOption === AUTOMATIC.value &&
      !clientNumberExists
    ) {
      switch (props.memberIdentifierType) {
        case "CUSTOM":
          return (
            <Field
              id="memberIdentifierInitialLength"
              name="memberIdentifierInitialLength"
              disabled={clientNumberExists || !props.create}
              component={SelectInput}
              addBlankOption={true}
              options={getOptions()}
              label={
                <FormattedMessage id="clients.form.additionalConfigurations.memberIdentifierLength" />
              }
            />
          );
      }
    }
  };

  const clientNumberExists = props.clientNumberExists && props.businessAccountType == GM.value;
  const automaticIdentifierOptions = generateAutomaticIdentifierOptions();
  const isAutomaticMemberIdentifierDisabled = clientNumberExists || !props.create || (Array.isArray(automaticIdentifierOptions) && automaticIdentifierOptions.length == 1);
  return (
    <div className="ClientSubsection--fullWidth">
      <h3>
        <FormattedMessage id="clients.form.additionalConfigurations.title" />
      </h3>
      <Field
        groupClassName="wrapping"
        id="showDependentPersonFormula"
        name="showDependentPersonFormula"
        type="checkbox"
        component={StyledCheckboxInput}
        disabled={props.disabled}
        checkbox={{ value: true }}
        label={
          <FormattedHTMLMessage id="clients.form.additionalConfigurations.showDependentPersonFormula" />
        }
        normalize={(value) => !!value}
      />
      <Field
        groupClassName="wrapping"
        id="shouldImportWithoutId"
        name="shouldImportWithoutId"
        type="checkbox"
        disabled={props.disabled}
        component={StyledCheckboxInput}
        checkbox={{ value: true }}
        label={
          <FormattedHTMLMessage id="clients.form.additionalConfigurations.shouldImportWithoutId" />
        }
        normalize={(value) => !!value}
      />
      {props.businessAccountType == "undefined" && (
        <MessageBox className="alert-info">
          <FormattedMessage id="clients.form.additionalConfigurations.accountTypeSelection" />
        </MessageBox>
      )}
      {props.clientBillingPeriodType != MANUAL.value && userHasAccess(HIGH_MANAGEMENT_WITH_FINANCE_AND_MANAGER) && (
        <div
          className={`member-identifier-option-${userLanguage.split("-")[0]}`}
        >
          <Field
            id="syncType"
            name="syncType"
            disabled={!props.create}
            required={true}
            addBlankOption={true}
            component={SelectInput}
            options={props.syncTypeOptions}
            onChange={checkForSyncTypeChange}
            label={
              <FormattedMessage id="clients.form.additionalConfigurations.isSyncEligible" />
            }
          />
        </div>
      )}
      {userHasAccess(HIGH_MANAGEMENT_WITH_FINANCE_AND_MANAGER) && props.create &&
        props.syncType == ELIGIBILITY.value && (
          <MessageBox className="alert-warning">
            <FormattedMessage id="clients.form.additionalConfigurations.syncTypeWarning" />
          </MessageBox>
        )}

      {userHasAccess(HIGH_MANAGEMENT_WITH_FINANCE_AND_MANAGER) && props.create &&
        props.syncType == TED_DESJARDINS.value && (
          <MessageBox className="alert-warning">
            <FormattedMessage id="clients.form.additionalConfigurations.tedSyncWarning" />
          </MessageBox>
        )}
      {!clientNumberExists && (
        <span
          className={`member-identifier-option-${userLanguage.split("-")[0]}`}
        >
          <Field
            id="isAutomaticMemberIdentifier"
            name="isAutomaticMemberIdentifier"
            disabled={isAutomaticMemberIdentifierDisabled}
            addBlankOption={true}
            required={true}
            component={SelectInput}
            options={automaticIdentifierOptions}
            label={
              <FormattedMessage id="clients.form.additionalConfigurations.isAutomaticMemberIdentifier" />
            }
          />
        </span>
      )}
      {clientNumberExists && props.currentClientNumber && (
        <MessageBox className="alert-info">
          <FormattedMessage id="clients.form.additionalConfigurations.clientNumberExists" />
        </MessageBox>
      )}
      <div className="client-additional-configuration">
        {
          props.automaticMemberIdentifierOption === AUTOMATIC.value &&
          !clientNumberExists && (
            <Field
              props={{ onChange: props.onSelectMemberIdentifierType }}
              id="memberIdentifierType"
              name="memberIdentifierType"
              component={RadioGroup}
              disabled={clientNumberExists || !props.create}
              radios={[
                {
                  value: "DEFAULT",
                  text: (
                    <FormattedMessage id="clients.form.additionalConfigurations.defaultLength" />
                  ),
                },
                {
                  value: "CUSTOM",
                  text: (
                    <FormattedMessage id="clients.form.additionalConfigurations.customLength" />
                  ),
                },
              ]}
            />
          )}
        {generateOptions()}

        {!props.currentClientNumber &&
          props.automaticMemberIdentifierOption === AUTOMATIC.value && (
            <MessageBox className="alert-warning">
              <FormattedHTMLMessage id="validation.clientFieldValidation.missingClientNumberForAutomaticIdentifier" />
            </MessageBox>
          )}


        <Field
          groupClassName="wrapping"
          id="isPublishable"
          name="isPublishable"
          type="checkbox"
          disabled={props.disabled}
          component={StyledCheckboxInput}
          checkbox={{ value: true }}
          label={
            <FormattedHTMLMessage id="clients.form.additionalConfigurations.includeNotifications" />
          }
          normalize={(value) => !!value}
        />

        {/* Group field - hidden for external roles */}
        {userHasAccess(INTERNAL_ROLES) && (
          <div>
            <Field
              id="groupId"
              name="groupId"
              component={SelectInput}
              options={resolvedGroupOptions || []}
              clearable={true}
              searchable={true}
              componentClassName="group-select"
              disabled={isGroupFieldDisabled}
              label={<FormattedMessage id="clients.form.complement.groupLabel" />}
              optionalProps={{
                noResultsText: <FormattedMessage id="common.noResults" />,
                autosize: false,
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

ClientAdditionalConfigurations.propTypes = {
  disabled: PropTypes.bool,
  create: PropTypes.bool,
  clientBillingPeriodType: PropTypes.string,
  syncType: PropTypes.string,
  businessAccountType: PropTypes.string,
  memberIdentifierType: PropTypes.string,
  onSelectMemberIdentifierType: PropTypes.func,
  onAutomaticMemberIdentifier: PropTypes.func,
  clientNumberExists: PropTypes.bool,
  currentClientStatusType: PropTypes.string,
  automaticMemberIdentifierOption: PropTypes.string,
  currentClientNumber: PropTypes.string,
  changeFieldValue: PropTypes.func,
  automaticIdentifierConfigurationTypes: PropTypes.array,
  setClientNumberExistsState: PropTypes.func,
  syncTypeOptions: PropTypes.array,
  groupOptions: PropTypes.array,
  currentGroupOption: PropTypes.array,
  isArchived: PropTypes.bool
};

export default ClientAdditionalConfigurations;



// WEBPACK FOOTER //
// ./src/components/dashboard/clients/client-form/basic-client-sections/ClientAdditionalConfigurations.js